import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { LandingNavbar } from "@/components/landing-navbar";
import { Footer } from "@/components/footer";
import { SEO, schemas } from "@/components/SEO";
import { ArticleCard } from "./components/ArticleCard";
import { ArrowLeft, Lightbulb } from "lucide-react";

const articles = [
  {
    title: "Client Onboarding Workflow That Works: 5-Step SOP",
    excerpt:
      "A proven 5-step process to onboard new commercial cleaning clients, secure alarm codes, and prevent churn in the first 60 days.",
    readTime: 8,
    href: "/success-hub/article/commercial-cleaning-client-onboarding",
  },
  {
    title: "Optimal Staff Scheduling & Route Density Strategies",
    excerpt:
      "Balance cleaner workload, eliminate unbillable travel time, and optimize multi-facility route density with standard operating procedures.",
    readTime: 7,
    href: "/success-hub/article/commercial-cleaning-crew-scheduling-sop",
  },
  {
    title: "Using Document Templates Effectively: Commercial SOW & Proposals",
    excerpt:
      "Structure winning commercial cleaning proposals, itemized Scopes of Work (SOW), and legally binding service agreements that stop scope creep.",
    readTime: 6,
    href: "/success-hub/article/commercial-cleaning-scope-of-work-templates",
  },
  {
    title: "Commercial Cleaning Quality Control: 100-Point Inspection SOP",
    excerpt:
      "Implement standardized inspection checklists, photo-verified corrective action plans, and quality metrics to maintain 98%+ client retention.",
    readTime: 7,
    href: "/success-hub/article/janitorial-quality-control-inspection-sop",
  },
  {
    title: "Managing Multiple Locations: Scaling Without Chaos",
    excerpt:
      "Strategies for scaling your cleaning company across multiple facilities, managing geographic clusters, and centralizing building access codes.",
    readTime: 9,
    href: "/success-hub/article/multi-facility-janitorial-operations",
  },
  {
    title: "Building Strong Client Relationships: Retention Playbook",
    excerpt:
      "Proactive communication strategies, Quarterly Business Reviews (QBR), and automated review collection loops that stop account churn.",
    readTime: 6,
    href: "/success-hub/article/commercial-cleaning-client-retention-strategies",
  },
  {
    title: "Efficient Bid Creation & ISSA 612 Workloading Process",
    excerpt:
      "The complete 4-step estimating formula using cleanable square footage, ISSA 612 production rates, and protected profit margins.",
    readTime: 8,
    href: "/success-hub/article/commercial-cleaning-bidding-guide",
  },
  {
    title: "Janitorial Staff Training & Safety SOP: Zero-Defect Onboarding",
    excerpt:
      "Structured 4-day onboarding covering OSHA HazCom chemical safety, chemical dwell times, and color-coded microfiber cross-contamination.",
    readTime: 7,
    href: "/success-hub/article/janitorial-staff-training-safety-sop",
  },
  {
    title: "Inventory & Chemical Supply Management: Controlling Overhead",
    excerpt:
      "Keep chemical and consumable costs between 2% and 4% of revenue using dilution dispensers, PAR inventory levels, and weekly audits.",
    readTime: 6,
    href: "/success-hub/article/commercial-cleaning-inventory-supply-management",
  },
  {
    title: "Monthly Reporting for Commercial Clients: SLA Scorecards That Sell",
    excerpt:
      "Deliver automated monthly quality audit reports and SLA scorecards to property managers to prove value and secure 3-year renewals.",
    readTime: 5,
    href: "/success-hub/article/janitorial-client-monthly-reporting-audits",
  },
  {
    title: "Handling Commercial Cleaning Client Complaints Professionally",
    excerpt:
      "A proven 2-hour response protocol to handle commercial cleaning complaints, resolve facility issues, and turn unhappy accounts into 5-star references.",
    readTime: 6,
    href: "/success-hub/article/handling-commercial-cleaning-complaints",
  },
  {
    title: "Seasonal Workload Management: Deep Clean & Floor Care Scheduling",
    excerpt:
      "How to plan and sell seasonal janitorial services: winter floor care, spring window washing, and summer school strip & wax surges.",
    readTime: 7,
    href: "/success-hub/article/commercial-cleaning-seasonal-workload-management",
  },
];

export default function BestPracticesPage() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <SEO
        title="Best Practices | Commercial Cleaning Operations & Growth"
        description="Learn proven strategies for client onboarding, staff scheduling, quality control, and growing your cleaning business."
        keywords={["best practices", "tips", "strategies", "client management", "scheduling"]}
        url="/success-hub/best-practices"
        structuredData={[
          schemas.createBreadcrumbSchema([
            { name: "Home", url: "/" },
            { name: "Success Hub", url: "/success-hub" },
            { name: "Best Practices", url: "/success-hub/best-practices" },
          ]),
        ]}
      />
      <LandingNavbar />

      {/* Header */}
      <section className="pb-12 pt-28">
        <div className="container mx-auto px-4">
          <Link
            to="/success-hub"
            className="mb-6 inline-flex items-center text-sm text-slate-600 transition-colors hover:text-blue-600"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Success Hub
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex items-start gap-6"
          >
            <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-2xl bg-green-100">
              <Lightbulb className="h-8 w-8 text-green-600" />
            </div>
            <div>
              <h1 className="mb-3 text-3xl font-bold text-slate-900 md:text-4xl">Best Practices</h1>
              <p className="max-w-2xl text-lg text-slate-600">
                Learn proven strategies for client onboarding, staff scheduling, and quality
                control.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Articles List */}
      <section className="pb-20">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-3xl">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="space-y-4"
            >
              {articles.map((article, index) => (
                <ArticleCard
                  key={index}
                  title={article.title}
                  excerpt={article.excerpt}
                  category="Best Practices"
                  categoryColor="bg-green-100 text-green-700"
                  readTime={article.readTime}
                  href={(article as any).href || "/success-hub"}
                />
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
